import React, { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Menu,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import {
  ChevronDown,
  Download,
  Filter,
  Search as SearchIcon,
  Users,
  UserPlus,
  MessageSquare,
  Mail,
  X,
} from "lucide-react";
import { Parser } from "@json2csv/plainjs";
import { enqueueSnackbar } from "notistack";
import moment from "moment-timezone";
import { useSelector } from "react-redux";
import {
  PageHeader,
  PageContainer,
  StatCard,
  DataTable,
  EmptyState,
  Card,
  Input,
  Button,
  Badge,
} from "../../components/ui";
import { logMetaPixelEvent, isNullOrUndefined, isValidImage } from "../../utils";
import {
  BulkTagContacts,
  FetchInstagramContacts,
} from "../../Api/ApiEndPoints";
import { callNonStoreApi } from "../../NonStoreApis";
import userImg from "../../ImageAssets/user.jpg";

// Avatar with fallback — preserves the image validation flow used by
// InstagramContactNameAndUsername.js
function ContactAvatar({ src, alt }) {
  const [resolved, setResolved] = useState(src || userImg);
  useEffect(() => {
    let cancelled = false;
    async function resolve() {
      if (isNullOrUndefined(src)) {
        if (!cancelled) setResolved(userImg);
        return;
      }
      const ok = await isValidImage(src);
      if (!cancelled) setResolved(ok ? src : userImg);
    }
    resolve();
    return () => {
      cancelled = true;
    };
  }, [src]);
  return (
    <Box
      component="img"
      src={resolved}
      alt={alt || "avatar"}
      sx={{
        width: 32,
        height: 32,
        borderRadius: "50%",
        objectFit: "cover",
        flexShrink: 0,
      }}
    />
  );
}

// Tag chips with overflow — small, local component
function TagChips({ tags, limit = 2 }) {
  if (!Array.isArray(tags) || tags.length === 0) {
    return (
      <Typography component="span" sx={{ fontSize: 13, color: "#94a3b8" }}>
        —
      </Typography>
    );
  }
  const shown = tags.slice(0, limit);
  const extra = tags.length - shown.length;
  return (
    <Box sx={{ display: "inline-flex", flexWrap: "wrap", gap: "4px" }}>
      {shown.map((t, i) => (
        <Badge key={i} variant="neutral">
          {t}
        </Badge>
      ))}
      {extra > 0 && <Badge variant="neutral">+{extra}</Badge>}
    </Box>
  );
}

function formatRelative(date) {
  if (!date) return "—";
  try {
    return moment(date).tz("Asia/Kolkata").fromNow();
  } catch (e) {
    return moment(date).fromNow();
  }
}

const PAGE_SIZE = 20;

// ───────── Filter chip building blocks ─────────

const CHIP_BASE_SX = {
  display: "inline-flex",
  alignItems: "center",
  gap: "6px",
  height: 32,
  px: "12px",
  borderRadius: "8px",
  fontSize: 13,
  fontWeight: 500,
  fontFamily: "Inter, sans-serif",
  cursor: "pointer",
  border: "1px solid #e2e8f0",
  backgroundColor: "#ffffff",
  color: "#334155",
  whiteSpace: "nowrap",
  userSelect: "none",
  transition: "background-color 150ms ease-out, border-color 150ms ease-out",
  "&:hover": { backgroundColor: "#f8fafc" },
};

const CHIP_ACTIVE_SX = {
  border: "1px solid #fed7aa",
  backgroundColor: "#fff7ed",
  color: "#f97316",
  "&:hover": { backgroundColor: "#ffedd5" },
};

function ChipShell({ active, onClick, children, ariaLabel }) {
  return (
    <Box
      role="button"
      aria-label={ariaLabel}
      onClick={onClick}
      sx={{ ...CHIP_BASE_SX, ...(active ? CHIP_ACTIVE_SX : {}) }}
    >
      {children}
    </Box>
  );
}

function MenuChip({ label, value, isActive, onClear, options, onSelect }) {
  const [anchor, setAnchor] = useState(null);
  const open = Boolean(anchor);
  return (
    <>
      <ChipShell
        active={isActive}
        onClick={(e) => setAnchor(e.currentTarget)}
        ariaLabel={`${label} filter`}
      >
        <span>{value}</span>
        {isActive ? (
          <Box
            component="span"
            role="button"
            aria-label={`Clear ${label} filter`}
            onClick={(e) => {
              e.stopPropagation();
              onClear?.();
            }}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: 16,
              height: 16,
              borderRadius: "50%",
              "&:hover": { backgroundColor: "#fed7aa" },
            }}
          >
            <X size={12} />
          </Box>
        ) : (
          <ChevronDown size={14} />
        )}
      </ChipShell>
      <Menu
        anchorEl={anchor}
        open={open}
        onClose={() => setAnchor(null)}
        PaperProps={{
          sx: {
            borderRadius: "8px",
            border: "1px solid #e2e8f0",
            boxShadow:
              "0 4px 6px -2px rgba(16,24,40,0.05), 0 10px 15px -3px rgba(16,24,40,0.1)",
            minWidth: 180,
            maxHeight: 320,
          },
        }}
      >
        {options.map((opt) => (
          <MenuItem
            key={String(opt.value)}
            selected={opt.value === value || opt.label === value}
            onClick={() => {
              onSelect(opt);
              setAnchor(null);
            }}
            sx={{ fontSize: 13, color: "#0f172a" }}
          >
            {opt.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

function ToggleChip({ label, active, onClick }) {
  return (
    <ChipShell active={active} onClick={onClick} ariaLabel={`${label} toggle`}>
      <span>{label}</span>
      {active && (
        <Box
          component="span"
          role="button"
          aria-label={`Clear ${label} filter`}
          onClick={(e) => {
            e.stopPropagation();
            onClick?.();
          }}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: 16,
            height: 16,
            borderRadius: "50%",
            "&:hover": { backgroundColor: "#fed7aa" },
          }}
        >
          <X size={12} />
        </Box>
      )}
    </ChipShell>
  );
}

const DATE_RANGE_OPTIONS = [
  { value: "all", label: "All time", days: null },
  { value: "7d", label: "7d", days: 7 },
  { value: "30d", label: "30d", days: 30 },
  { value: "90d", label: "90d", days: 90 },
];

function InstagramContactsList() {
  const { credentials } = useSelector((state) => state.instagram);
  const [mainList, setMainList] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({});
  const [page, setPage] = useState(1);

  // Filter state
  const [automationFilter, setAutomationFilter] = useState("all"); // "all" or automationName
  const [dateRange, setDateRange] = useState("all"); // see DATE_RANGE_OPTIONS
  const [hasEmail, setHasEmail] = useState(false);
  const [hasPhone, setHasPhone] = useState(false);
  const [tagFilter, setTagFilter] = useState("all"); // "all" or tag string

  // Selection state (Set of row ids)
  const [selectedIds, setSelectedIds] = useState(new Set());

  // Tag dialog state
  const [tagDialogOpen, setTagDialogOpen] = useState(false);
  const [tagInput, setTagInput] = useState("");
  const [tagApplying, setTagApplying] = useState(false);

  useEffect(() => {
    logMetaPixelEvent("ViewContent");
  }, []);

  const fetchContacts = React.useCallback(async () => {
    if (isNullOrUndefined(credentials)) return;
    setLoading(true);
    try {
      const response = await callNonStoreApi(FetchInstagramContacts, {
        instaUserId: credentials.user_id,
      });
      setMainList(response?.data?.contacts || []);
      setStats(response?.data?.stats || {});
    } catch (e) {
      setMainList([]);
      setStats({});
    } finally {
      setLoading(false);
    }
  }, [credentials]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (cancelled) return;
      await fetchContacts();
    })();
    return () => {
      cancelled = true;
    };
  }, [fetchContacts]);

  // Stable id resolver — used both for selection and CSV key collisions
  const getRowId = React.useCallback(
    (r) => r._id || r.id || r.user_id || r.username || JSON.stringify(r),
    []
  );

  // Derive automation options from data
  const automationOptions = useMemo(() => {
    const seen = new Set();
    const opts = [{ value: "all", label: "All automations" }];
    mainList.forEach((c) => {
      if (c.automationName && !seen.has(c.automationName)) {
        seen.add(c.automationName);
        opts.push({ value: c.automationName, label: c.automationName });
      }
    });
    return opts;
  }, [mainList]);

  // Derive tag options from data
  const tagOptions = useMemo(() => {
    const seen = new Set();
    const opts = [{ value: "all", label: "All tags" }];
    mainList.forEach((c) => {
      if (Array.isArray(c.tags)) {
        c.tags.forEach((t) => {
          const tag = String(t);
          if (tag && !seen.has(tag)) {
            seen.add(tag);
            opts.push({ value: tag, label: tag });
          }
        });
      }
    });
    return opts;
  }, [mainList]);

  // Tag suggestions for the dialog (no "All tags" wrapper)
  const tagSuggestions = useMemo(
    () => tagOptions.filter((o) => o.value !== "all").map((o) => o.value),
    [tagOptions]
  );

  // Apply all filters client-side
  const filtered = useMemo(() => {
    const now = Date.now();
    const dateOpt = DATE_RANGE_OPTIONS.find((o) => o.value === dateRange);
    const cutoff =
      dateOpt && dateOpt.days != null
        ? now - dateOpt.days * 24 * 60 * 60 * 1000
        : null;
    const q = search.trim().toLowerCase();

    return mainList.filter((c) => {
      // Search
      if (q) {
        const matchesSearch =
          c.name?.toLowerCase().includes(q) ||
          c.username?.toLowerCase().includes(q) ||
          (Array.isArray(c.tags) &&
            c.tags.some((t) => String(t).toLowerCase().includes(q)));
        if (!matchesSearch) return false;
      }
      // Automation
      if (automationFilter !== "all" && c.automationName !== automationFilter) {
        return false;
      }
      // Date range
      if (cutoff != null) {
        if (!c.date) return false;
        if (new Date(c.date).getTime() < cutoff) return false;
      }
      // Has email
      if (hasEmail && !c.email) return false;
      // Has phone
      if (hasPhone && !c.phoneNumber) return false;
      // Tag
      if (tagFilter !== "all") {
        if (!Array.isArray(c.tags) || !c.tags.some((t) => String(t) === tagFilter)) {
          return false;
        }
      }
      return true;
    });
  }, [
    mainList,
    search,
    automationFilter,
    dateRange,
    hasEmail,
    hasPhone,
    tagFilter,
  ]);

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [search, automationFilter, dateRange, hasEmail, hasPhone, tagFilter]);

  const total = filtered.length;
  const pagedRows = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, page]);

  // Stat row — derive from loaded data
  const sevenDaysAgo = useMemo(
    () => Date.now() - 7 * 24 * 60 * 60 * 1000,
    []
  );
  const newThisWeek = useMemo(
    () =>
      mainList.filter(
        (c) => c.date && new Date(c.date).getTime() >= sevenDaysAgo
      ).length,
    [mainList, sevenDaysAgo]
  );
  const engaged = useMemo(
    () =>
      mainList.filter(
        (c) =>
          c.date &&
          new Date(c.date).getTime() >= sevenDaysAgo &&
          Array.isArray(c.mediaIds) &&
          c.mediaIds.length > 0
      ).length,
    [mainList, sevenDaysAgo]
  );
  const withEmail = useMemo(
    () => mainList.filter((c) => c.email).length,
    [mainList]
  );

  // CSV export — given a list of contacts, render and download
  const exportRows = (rowsToExport, filename) => {
    try {
      if (!rowsToExport || rowsToExport.length === 0) {
        return enqueueSnackbar("No data to export", { variant: "error" });
      }
      const parser = new Parser();
      const rows = rowsToExport.map((item, index) => ({
        "Sl. No.": index + 1,
        Name: item.name,
        Username: item.username,
        Email: item.email,
        "Phone Number": item.phoneNumber,
        Tags: Array.isArray(item.tags) ? item.tags.join(", ") : "",
        "From automation": item.automationName || "",
        Date: item.date
          ? moment(item.date).tz("Asia/Kolkata").format("DD MMM YYYY")
          : "-",
      }));
      const csv = parser.parse(rows);
      const element = document.createElement("a");
      element.setAttribute("href", `data:text/csv;charset=utf-8,${csv}`);
      element.setAttribute("download", filename);
      element.style.display = "none";
      document.body.appendChild(element);
      element.click();
      document.body.removeChild(element);
    } catch (e) {
      enqueueSnackbar("Export failed", { variant: "error" });
    }
  };

  const handleExport = () => exportRows(filtered, "Instagram_Leads");

  const handleExportSelected = () => {
    const rowsToExport = mainList.filter((r) => selectedIds.has(getRowId(r)));
    exportRows(rowsToExport, "Instagram_Leads_Selected");
  };

  const clearSelection = () => setSelectedIds(new Set());

  const openTagDialog = () => {
    setTagInput("");
    setTagDialogOpen(true);
  };

  const closeTagDialog = () => {
    if (tagApplying) return;
    setTagDialogOpen(false);
  };

  const applyBulkTag = async () => {
    const tag = tagInput.trim();
    if (!tag) {
      return enqueueSnackbar("Enter a tag name", { variant: "error" });
    }
    if (tag.length > 50) {
      return enqueueSnackbar("Tag must be 50 characters or fewer", {
        variant: "error",
      });
    }
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    setTagApplying(true);
    try {
      const res = await callNonStoreApi(BulkTagContacts, {
        contactIds: ids,
        tag,
      });
      const updated =
        res?.data?.updatedCount != null ? res.data.updatedCount : ids.length;
      enqueueSnackbar(`Tagged ${updated} leads as '${tag}'`, {
        variant: "success",
      });
      setTagDialogOpen(false);
      clearSelection();
      await fetchContacts();
    } catch (e) {
      enqueueSnackbar(
        e?.response?.data?.error || e?.message || "Failed to tag leads",
        { variant: "error" }
      );
    } finally {
      setTagApplying(false);
    }
  };

  const columns = [
    {
      key: "username",
      header: "Username",
      render: (r) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <ContactAvatar src={r.profile_pic} alt={r.username} />
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: 14,
                fontWeight: 500,
                color: "#0f172a",
                lineHeight: 1.3,
              }}
            >
              {r.name || "Instagram User"}
            </Typography>
            {r.username && (
              <Typography sx={{ fontSize: 12, color: "#64748b" }}>
                @{r.username}
              </Typography>
            )}
          </Box>
        </Box>
      ),
    },
    {
      key: "email",
      header: "Email",
      render: (r) =>
        r.email ? (
          <Typography sx={{ fontSize: 13, color: "#334155" }}>
            {r.email}
          </Typography>
        ) : (
          <Typography sx={{ fontSize: 13, color: "#94a3b8" }}>—</Typography>
        ),
      priority: "low",
    },
    {
      key: "phone",
      header: "Phone",
      render: (r) => (
        <Typography sx={{ fontSize: 13, color: r.phoneNumber ? "#334155" : "#94a3b8" }}>
          {r.phoneNumber || "—"}
        </Typography>
      ),
      priority: "low",
    },
    {
      key: "tags",
      header: "Tags",
      render: (r) => <TagChips tags={r.tags} limit={2} />,
      priority: "low",
    },
    {
      key: "automation",
      header: "From automation",
      render: (r) => (
        <Typography sx={{ fontSize: 13, color: "#64748b" }}>
          {r.automationName || "—"}
        </Typography>
      ),
      priority: "low",
    },
    {
      key: "lastActivity",
      header: "Last activity",
      render: (r) => (
        <Typography sx={{ fontSize: 13, color: "#475569" }}>
          {formatRelative(r.date)}
        </Typography>
      ),
    },
  ];

  const total_count = stats?.comments?.total?.count ?? mainList.length;

  // Active automation chip label
  const automationChipLabel =
    automationFilter === "all" ? "All automations" : automationFilter;
  const automationActive = automationFilter !== "all";

  // Active date range
  const dateRangeOpt =
    DATE_RANGE_OPTIONS.find((o) => o.value === dateRange) ||
    DATE_RANGE_OPTIONS[0];
  const dateRangeActive = dateRange !== "all";

  // Active tag chip
  const tagChipLabel = tagFilter === "all" ? "All tags" : tagFilter;
  const tagActive = tagFilter !== "all";

  const selectedCount = selectedIds.size;

  return (
    <>
      <PageHeader
        title="Leads"
        subtitle="Instagram users captured by your automations — export and segment for follow-up."
        actions={
          <>
            <Button
              variant="secondary"
              size="md"
              leftIcon={<Filter size={16} />}
              // TODO: wire filter drawer once filters outgrow the chip row.
              onClick={() => {
                // Scroll to chip row for now; chips are the live filter UI.
                const el = document.getElementById("leads-filter-chips");
                if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
              }}
            >
              Filter
            </Button>
            <Button
              variant="primary"
              size="md"
              leftIcon={<Download size={16} />}
              onClick={handleExport}
            >
              Export
            </Button>
          </>
        }
      />
      <PageContainer>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} md={3}>
            <StatCard
              icon={Users}
              label="Total leads"
              value={total_count}
              loading={loading}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <StatCard
              icon={UserPlus}
              label="New (7d)"
              value={newThisWeek}
              loading={loading}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <StatCard
              icon={MessageSquare}
              label="Engaged (7d)"
              value={engaged}
              loading={loading}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <StatCard
              icon={Mail}
              label="Email collected"
              value={withEmail}
              loading={loading}
            />
          </Grid>
        </Grid>

        {/* Filter chips + search */}
        <Card padding="sm" sx={{ mb: 2 }} id="leads-filter-chips">
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <MenuChip
              label="Automation"
              value={automationChipLabel}
              isActive={automationActive}
              onClear={() => setAutomationFilter("all")}
              options={automationOptions}
              onSelect={(opt) => setAutomationFilter(opt.value)}
            />
            <MenuChip
              label="Date range"
              value={dateRangeOpt.label}
              isActive={dateRangeActive}
              onClear={() => setDateRange("all")}
              options={DATE_RANGE_OPTIONS.map((o) => ({
                value: o.value,
                label: o.label,
              }))}
              onSelect={(opt) => setDateRange(opt.value)}
            />
            <ToggleChip
              label="Has email"
              active={hasEmail}
              onClick={() => setHasEmail((v) => !v)}
            />
            <ToggleChip
              label="Has phone"
              active={hasPhone}
              onClick={() => setHasPhone((v) => !v)}
            />
            <MenuChip
              label="Tag"
              value={tagChipLabel}
              isActive={tagActive}
              onClear={() => setTagFilter("all")}
              options={tagOptions}
              onSelect={(opt) => setTagFilter(opt.value)}
            />
            <Box sx={{ flex: 1, minWidth: 200 }}>
              <Input
                placeholder="Search by username or tag..."
                leftIcon={<SearchIcon size={16} color="#94a3b8" />}
                fullWidth
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                size="small"
              />
            </Box>
          </Box>
          <Box
            sx={{
              mt: "10px",
              fontSize: 13,
              color: "#64748b",
            }}
          >
            Showing {total} of {mainList.length} leads
          </Box>
        </Card>

        {/* Bulk action bar */}
        {selectedCount > 0 && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              backgroundColor: "#0f172a",
              color: "#ffffff",
              borderRadius: "12px",
              px: "16px",
              py: "10px",
              mb: 2,
              gap: "12px",
              flexWrap: "wrap",
            }}
            role="region"
            aria-label="Bulk actions"
          >
            <Box sx={{ fontSize: 14, fontWeight: 500 }}>
              {selectedCount} {selectedCount === 1 ? "lead" : "leads"} selected
            </Box>
            <Box sx={{ display: "flex", gap: "8px", alignItems: "center" }}>
              <Box
                role="button"
                onClick={clearSelection}
                sx={{
                  fontSize: 13,
                  color: "#cbd5e1",
                  cursor: "pointer",
                  px: "10px",
                  py: "6px",
                  borderRadius: "8px",
                  "&:hover": { backgroundColor: "#1e293b", color: "#ffffff" },
                }}
              >
                Clear
              </Box>
              <Box
                role="button"
                onClick={openTagDialog}
                sx={{
                  fontSize: 13,
                  fontWeight: 500,
                  color: "#ffffff",
                  cursor: "pointer",
                  px: "12px",
                  py: "6px",
                  borderRadius: "8px",
                  border: "1px solid #334155",
                  "&:hover": { backgroundColor: "#1e293b" },
                }}
              >
                Tag selected
              </Box>
              <Box
                role="button"
                onClick={handleExportSelected}
                sx={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: "#ffffff",
                  cursor: "pointer",
                  px: "12px",
                  py: "6px",
                  borderRadius: "8px",
                  backgroundColor: "#f97316",
                  "&:hover": { backgroundColor: "#ea580c" },
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "6px",
                }}
              >
                <Download size={14} />
                Export selected
              </Box>
            </Box>
          </Box>
        )}

        {/* Table, ARIA role="table" kept inherent via HTML <table> */}
        <Box role="table" aria-label="Leads">
          <DataTable
            columns={columns}
            rows={pagedRows}
            rowKey={(r) => getRowId(r)}
            getRowId={getRowId}
            selectable
            selectedIds={selectedIds}
            onSelectionChange={setSelectedIds}
            loading={loading}
            empty={
              <EmptyState
                icon={Users}
                title="No leads yet"
                description="Leads captured by your automations will appear here. Export them to your email list or CRM."
              />
            }
            pagination={{
              page,
              pageSize: PAGE_SIZE,
              total,
              onChange: setPage,
            }}
          />
        </Box>
      </PageContainer>

      {/* Tag dialog */}
      <Dialog
        open={tagDialogOpen}
        onClose={closeTagDialog}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: { borderRadius: "12px" },
        }}
      >
        <DialogTitle sx={{ fontSize: 16, fontWeight: 600, color: "#0f172a" }}>
          Tag leads
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 13, color: "#64748b", mb: 2 }}>
            Apply a tag to the {selectedCount} selected{" "}
            {selectedCount === 1 ? "lead" : "leads"}.
          </Typography>
          <Autocomplete
            freeSolo
            options={tagSuggestions}
            inputValue={tagInput}
            onInputChange={(_, val) => setTagInput(val)}
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                placeholder="Type a tag (e.g. VIP)"
                autoFocus
              />
            )}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            variant="ghost"
            size="sm"
            onClick={closeTagDialog}
            disabled={tagApplying}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={applyBulkTag}
            loading={tagApplying}
            disabled={!tagInput.trim()}
          >
            Apply tag
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default InstagramContactsList;
